import { useMemo, useState } from "react";
import { formatCurrency } from "../../../../Utils/formatters";
import { MastercardLogo } from "./MastercardLogo";
import { VoucherContextActions } from "./VoucherContextActions";
import { VoucherExtractSection } from "./VoucherExtractSection";
import { VoucherModal } from "./VoucherModal";
import { VoucherTransferForm } from "./VoucherTransferForm";
import {
  buildVoucherCategories,
  getTotalVoucherBalance,
  INITIAL_VOUCHER_BALANCES,
} from "./voucherCategories";

export const VoucherBenefitsPanel = () => {
  const [balances, setBalances] = useState(INITIAL_VOUCHER_BALANCES);
  const [selectedId, setSelectedId] = useState("supermarket");
  const [transferCategoryId, setTransferCategoryId] = useState(null);

  const categories = useMemo(() => buildVoucherCategories(balances), [balances]);
  const selectedCategory = categories.find((cat) => cat.id === selectedId) || categories[0];
  const transferCategory = categories.find((cat) => cat.id === transferCategoryId);
  const totalAvailable = formatCurrency(getTotalVoucherBalance(balances));

  const handleTransfer = ({ fromId, toId, amountCents }) => {
    const sourceBalance = balances[fromId] ?? 0;
    if (amountCents <= 0 || amountCents > sourceBalance) return false;

    setBalances((prev) => ({
      ...prev,
      [fromId]: prev[fromId] - amountCents,
      [toId]: (prev[toId] ?? 0) + amountCents,
    }));

    return true;
  };

  return (
    <div className="space-y-5">
      <section className="cartoes-voucher-card app-card p-4 sm:p-5">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
              Cartão voucher
            </p>
            <h3 className="text-base font-bold text-brand-dark">Benefícios</h3>
            <p className="mt-0.5 text-xs text-gray-500">Saldo total · Final 7392</p>
          </div>

          <div className="flex items-center gap-3 rounded-xl border border-gray-100 bg-brand-surface px-4 py-3">
            <MastercardLogo className="h-5 w-auto" />
            <div>
              <p className="text-[11px] text-gray-500">Disponível</p>
              <p className="text-sm font-bold text-brand-dark">{totalAvailable}</p>
            </div>
          </div>
        </div>
      </section>

      <section>
        <div className="mb-3">
          <h3 className="text-sm font-bold text-brand-dark">Caixinhas de benefícios</h3>
          <p className="mt-0.5 text-xs text-gray-500">
            Selecione uma caixinha para ver extrato e ações.
          </p>
        </div>

        <div className="cartoes-voucher-grid">
          {categories.map((category) => {
            const Icon = category.icon;
            const isSelected = category.id === selectedCategory?.id;

            return (
              <article
                key={category.id}
                className={`cartoes-voucher-box ${
                  isSelected ? "cartoes-voucher-box--selected" : ""
                }`}
              >
                <button
                  type="button"
                  className="cartoes-voucher-box__main"
                  onClick={() => setSelectedId(category.id)}
                >
                  <div className="cartoes-voucher-box__head">
                    <div className="cartoes-voucher-box__icon">
                      <Icon className="h-4 w-4 text-[#e7a73f]" />
                    </div>
                    <span className="cartoes-voucher-box__label">{category.label}</span>
                  </div>

                  <p className="cartoes-voucher-box__balance">
                    {formatCurrency(category.balanceCents)}
                  </p>
                  <p className="cartoes-voucher-box__total">
                    de {formatCurrency(category.totalCents)}
                  </p>

                  <div className="cartoes-progress mt-3">
                    <div className="cartoes-progress__track">
                      <div
                        className="cartoes-progress__fill cartoes-progress__fill--gold"
                        style={{ width: `${category.percent}%` }}
                      />
                    </div>
                    <p className="cartoes-progress__label">{category.percent}% disponível</p>
                  </div>
                </button>
              </article>
            );
          })}
        </div>
      </section>

      {selectedCategory && (
        <>
          <section className="cartoes-overview app-card">
            <div className="cartoes-overview__body">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                Visão geral
              </p>
              <h2 className="mt-1 text-lg font-bold text-brand-dark">
                {selectedCategory.label}
              </h2>
              <p className="mt-1 text-sm font-semibold text-brand-dark">
                {formatCurrency(selectedCategory.balanceCents)}
              </p>
              <p className="mt-0.5 text-xs text-gray-500">
                de {formatCurrency(selectedCategory.totalCents)} · {selectedCategory.percent}%
                disponível
              </p>
            </div>
            <div className="cartoes-overview__actions">
              <VoucherContextActions
                onTransfer={() => setTransferCategoryId(selectedCategory.id)}
              />
            </div>
          </section>

          <VoucherExtractSection
            categoryLabel={selectedCategory.label}
            categoryId={selectedCategory.id}
          />
        </>
      )}

      <VoucherModal
        open={Boolean(transferCategory)}
        onClose={() => setTransferCategoryId(null)}
        title={`Transferir · ${transferCategory?.label || ""}`}
      >
        {transferCategory && (
          <VoucherTransferForm
            fromCategory={transferCategory}
            balances={balances}
            onTransfer={handleTransfer}
            onSuccess={() => setTransferCategoryId(null)}
          />
        )}
      </VoucherModal>
    </div>
  );
};
